/**
 * Chronométrage AUTOMATIQUE de toutes les requêtes TanStack Query.
 *
 * Un seul point d'accroche, sur le cache : aucun hook n'a besoin d'être
 * instrumenté, donc aucune requête ne peut être oubliée — y compris celles
 * ajoutées plus tard. C'est ce qui rend le waterfall du premier chargement
 * lisible sans relire le code.
 *
 * Rien n'est posé quand l'interrupteur `?debug=perf` est éteint : `main.tsx`
 * n'appelle cette fonction que si `bootstrapPerf()` a renvoyé true.
 */

import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { isPerfEnabled, perf, sanitizePerfName } from "./perf";

/** Trois segments base64url séparés par des points : un jeton JWT. */
const JWT_RE = /eyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]+/g;

/**
 * Au-delà, un segment de clé n'est plus un identifiant lisible mais une valeur
 * opaque — donc un secret potentiel.
 */
const MAX_PART_LENGTH = 40;

/**
 * Neutralise ce qui ne doit jamais atterrir dans un rapport partagé.
 *
 * Deux niveaux, parce que le premier ne suffit pas :
 *   1. les formats connus (JWT, UUID, email) sont remplacés par un marqueur ;
 *   2. tout segment restant anormalement long est tronqué — filet pour un
 *      secret d'un format qu'on n'a pas anticipé.
 *
 * Vécu : la clé `["invitation-settlement", token]` a exposé un jeton d'accès
 * complet (email inclus dans sa charge utile) dans un rapport copié-collé. Le
 * masquage UUID/email seul ne l'avait pas vu.
 */
function redactPart(raw: string): string {
  const masked = sanitizePerfName(raw.replace(JWT_RE, "<jwt>"));
  if (masked.length <= MAX_PART_LENGTH) return masked;
  return `${masked.slice(0, MAX_PART_LENGTH - 3)}…<tronqué>`;
}

/**
 * Nom de span dérivé de la clé de requête, secrets et identifiants masqués.
 *
 * Les clés contiennent l'id d'organisation, parfois un id d'entité, et dans un
 * cas le jeton de session : un rapport collé dans un ticket ne doit rien
 * exposer de tout ça.
 */
export function queryPerfSpanName(key: QueryKey): string {
  const parts = (Array.isArray(key) ? key : [key]).map((part) => {
    if (typeof part === "string") return redactPart(part);
    if (typeof part === "number" || typeof part === "boolean") {
      return String(part);
    }
    if (part === null || part === undefined) return "-";
    try {
      return redactPart(JSON.stringify(part));
    } catch {
      return "?";
    }
  });
  return `query:${parts.join("/")}`;
}

/** Nombre de lignes quand la réponse est une liste — sinon `undefined`. */
function rowCount(data: unknown): number | undefined {
  return Array.isArray(data) ? data.length : undefined;
}

/**
 * Abonne le traceur au cache de requêtes. Retourne la fonction de
 * désabonnement (utilisée par les tests ; en production la page se recharge).
 */
export function installQueryPerfTracking(client: QueryClient): () => void {
  const startedAt = new Map<string, number>();

  const unsubscribe = client.getQueryCache().subscribe((event) => {
    // Deuxième garde : l'interrupteur peut être coupé en cours de session via
    // le bouton de l'overlay, sans recharger la page.
    if (!isPerfEnabled()) return;
    if (event.type !== "updated") return;

    const actionType = event.action?.type;
    const hash = event.query.queryHash;

    if (actionType === "fetch") {
      startedAt.set(hash, performance.now());
      return;
    }

    if (actionType !== "success" && actionType !== "error") return;

    const start = startedAt.get(hash);
    if (start === undefined) return;
    startedAt.delete(hash);

    const meta: Record<string, string | number | boolean | null> = {
      status: actionType,
    };
    const rows = rowCount(event.query.state.data);
    if (rows !== undefined) meta.rows = rows;

    perf.record(
      queryPerfSpanName(event.query.queryKey),
      start,
      performance.now(),
      meta,
    );
  });

  return () => {
    startedAt.clear();
    unsubscribe();
  };
}
